import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { 
  Search, 
  MapPin, 
  GraduationCap,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Bell,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const conferences = ['SWAC', 'MEAC', 'CIAA', 'SIAC'];
const confColors = {
  SWAC: 'bg-blue-100 text-blue-800',
  MEAC: 'bg-green-100 text-green-800',
  CIAA: 'bg-purple-100 text-purple-800',
  SIAC: 'bg-amber-100 text-amber-800',
};

const HBCUPage = () => {
  const [programs, setPrograms] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confFilter, setConfFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [progRes, alertRes] = await Promise.all([
          axios.get(`${API_URL}/hbcu`),
          axios.get(`${API_URL}/hbcu/scholarships`)
        ]);
        setPrograms(progRes.data.programs || []);
        setAlerts(alertRes.data.alerts || []);
      } catch (err) {
        console.error('Failed to fetch HBCU data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = programs.filter(p => {
    if (confFilter !== 'all' && p.conference !== confFilter) return false;
    if (genderFilter !== 'all' && !p.programs?.includes(genderFilter)) return false;
    if (search && !p.school?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const confCounts = {};
  conferences.forEach(c => { confCounts[c] = programs.filter(p => p.conference === c).length; });

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Building the Pipeline</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="hbcu-title">
            HBCU Golf Programs
          </h1>
          <p className="text-cream/70 font-body max-w-2xl">
            Connecting junior golfers and families directly to HBCU college coaches. Discover programs, scholarships, and opportunities.
          </p>
        </div>
      </div>

      <div className="container-padding">
        {/* Disclaimers */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-3" data-testid="hbcu-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-amber-800 text-xs font-body">
            <strong>Notice:</strong> Fairway Alliance provides this directory as a resource. We are not responsible for, nor do we guarantee, outcomes of any recruiting process. <strong>Always consult NCAA/NAIA eligibility guidelines before contacting coaches.</strong>
          </p>
        </div>

        {/* Scholarship Alerts */}
        {alerts.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-5 h-5 text-gold" />
              <h2 className="font-display font-bold text-lg text-deep-green">Scholarship Alerts</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {alerts.map(alert => (
                <Card key={alert.alert_id || alert.id} className="bg-white border-0 shadow-sm" data-testid="scholarship-alert-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={confColors[alert.conference] || 'bg-gray-100 text-gray-800'}>
                        {alert.conference}
                      </Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Deadline: {new Date(alert.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="font-display font-semibold text-sm text-deep-green mb-1">{alert.title}</h3>
                    <p className="text-muted-foreground text-xs font-body line-clamp-2">{alert.description}</p>
                    {alert.url && (
                      <a href={alert.url} target="_blank" rel="noopener noreferrer" className="text-gold text-xs font-semibold hover:underline mt-2 inline-block">
                        Learn More <ExternalLink className="w-3 h-3 inline ml-1" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search schools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white"
              data-testid="hbcu-search"
            />
          </div>
          <Select value={confFilter} onValueChange={setConfFilter}>
            <SelectTrigger className="w-full md:w-44 bg-white" data-testid="hbcu-conf-filter">
              <SelectValue placeholder="Conference" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Conferences</SelectItem>
              {conferences.map(c => <SelectItem key={c} value={c}>{c} ({confCounts[c]})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={genderFilter} onValueChange={setGenderFilter}>
            <SelectTrigger className="w-full md:w-40 bg-white" data-testid="hbcu-gender-filter">
              <SelectValue placeholder="Program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Programs</SelectItem>
              <SelectItem value="Men">Men's Golf</SelectItem>
              <SelectItem value="Women">Women's Golf</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Conference Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {conferences.map(c => (
            <button
              key={c}
              onClick={() => setConfFilter(confFilter === c ? 'all' : c)}
              className={`p-4 rounded-sm text-center transition-all ${
                confFilter === c ? 'bg-fairway text-white shadow-md' : 'bg-white hover:shadow-md'
              }`}
              data-testid={`conf-btn-${c.toLowerCase()}`}
            >
              <p className={`font-display font-bold text-2xl ${confFilter === c ? 'text-gold' : 'text-deep-green'}`}>
                {confCounts[c]}
              </p>
              <p className={`text-xs font-body ${confFilter === c ? 'text-cream/70' : 'text-muted-foreground'}`}>
                {c} Programs
              </p>
            </button>
          ))}
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((prog) => (
            <Card key={prog.program_id || prog.id} className="bg-white border-0 shadow-sm hover:shadow-md transition-all" data-testid="hbcu-program-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-fairway rounded-full flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-gold" />
                    </div>
                    <Badge className={confColors[prog.conference] || 'bg-gray-100'}>
                      {prog.conference}
                    </Badge>
                  </div>
                  {prog.scholarships_available && (
                    <Badge className="bg-green-100 text-green-800 text-xs">Scholarships</Badge>
                  )}
                </div>

                <h3 className="font-display font-bold text-lg text-deep-green mb-1">{prog.school}</h3>
                <p className="text-muted-foreground text-sm flex items-center gap-1 mb-2">
                  <MapPin className="w-3 h-3" /> {prog.city}, {prog.state}
                </p>

                <div className="flex items-center gap-4 mb-3">
                  <span className="text-xs text-muted-foreground">{prog.division}</span>
                  <div className="flex gap-1">
                    {prog.programs?.map(g => (
                      <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                    ))}
                  </div>
                </div>

                {prog.website && (
                  <a href={prog.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full border-fairway text-fairway hover:bg-fairway hover:text-white text-xs" data-testid="hbcu-website-btn">
                      Visit Athletics Site <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-display text-lg">No programs found matching your criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HBCUPage;
